/**
 * PreparationCCS.jsx — Hub dédié « Futur enseignant CCS 2 Roues ».
 *
 * Audience cible : candidat préparant l'examen CCS 2 Roues (ECSR).
 *
 * Route publique : /preparation-ccs
 *
 * Approche : agrégateur des outils CCS déjà existants, organisés en un
 * parcours pédagogique 6 étapes (Comprendre → Maîtriser → S'entraîner →
 * Simuler → Approfondir → Évaluer). Mobile-first, dans l'esprit des autres
 * hubs (Teacher.jsx, CCSJuryHub.jsx).
 */
import React from 'react';
import { Link } from 'react-router-dom';
import {
  GraduationCap, BookOpen, ListChecks, Mic, FlaskConical, Trophy,
  ArrowRight, Compass, Target, ScrollText, Library, Sparkles,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import useSeo from '../hooks/useSeo';
import AudienceContextBanner from '../components/AudienceContextBanner';
import SecuriteRoutiereStats from '../components/SecuriteRoutiereStats';

const ACCENT = '#ea580c'; // orange — distinct des espaces élève (rose) et enseignant (cyan)

/* ── Étapes du parcours futur enseignant ────────────────────────── */
const ETAPES = [
  {
    n: 1,
    icon: Compass,
    titre: 'Comprendre le CCS 2 Roues',
    sousTitre: 'Cadre, jury, attendus, déroulé de l\'examen',
    cibles: ['Structure des 6 modules', 'Critères de notation', 'Profil jury'],
    cta: 'Voir le référentiel',
    to: '/enseignant/referentiel',
    accent: '#0891b2',
  },
  {
    n: 2,
    icon: BookOpen,
    titre: 'Maîtriser le REMC',
    sousTitre: 'Le référentiel pédagogique officiel C1-C2-C3-C4',
    cibles: ['Sous-compétences détaillées', 'Critères observables', 'Mapping aux exercices'],
    cta: 'Explorer le REMC',
    to: '/enseignant/ccs-jury',
    accent: '#7c3aed',
  },
  {
    n: 3,
    icon: ListChecks,
    titre: 'S\'entraîner sur les 27 études de cas',
    sousTitre: 'Les cas officiels de l\'oral CCS — théorie + pratique',
    cibles: ['Cas Plateau (C1)', 'Cas Circulation (C2-C3-C4)', 'Cas Réglementation'],
    cta: 'Travailler les cas',
    to: '/enseignant/etudes-de-cas',
    accent: '#dc2626',
  },
  {
    n: 4,
    icon: Mic,
    titre: 'Banque de questions jury',
    sousTitre: '105 questions classées en 6 thèmes pour l\'oral',
    cibles: ['Pédagogie', 'Réglementation', 'Sécurité', 'Évaluation', 'Posture', 'Animation'],
    cta: 'Voir la banque',
    to: '/enseignant/banque-questions-jury',
    accent: '#16a34a',
  },
  {
    n: 5,
    icon: FlaskConical,
    titre: 'Simuler l\'oral du CCS',
    sousTitre: 'Examen blanc guidé avec Jury Virtuel IA (6 modules)',
    cibles: ['Tirage au sort cas', 'Préparation chronométrée', 'Évaluation IA détaillée'],
    cta: 'Lancer le simulateur',
    to: '/enseignant/simulateur-ccs',
    accent: '#ea580c',
    highlight: true,
  },
  {
    n: 6,
    icon: Trophy,
    titre: 'Tester ses connaissances',
    sousTitre: 'Grand Quiz Studio · 27 cas en mode équipes',
    cibles: ['Quiz interactif', 'Mécaniques variées', 'Mode projection classe'],
    cta: 'Jouer au Grand Quiz',
    to: '/enseignant/grand-quiz',
    accent: '#f59e0b',
  },
];

/* ── Ressources annexes ─────────────────────────────────────────── */
const RESSOURCES = [
  {
    icon: Sparkles,
    titre: '🌟 Top 20 cours simples à donner à l\'oral',
    sousTitre: '10 « cours faciles » + 10 « Trio Regard · Trajectoire · Allure »',
    to: '/enseignant/ccs-jury',
    highlight: true,
  },
  {
    icon: Library,
    titre: 'Fiches CCS Jury Expert',
    sousTitre: '32 fiches officielles (12 plateau + 20 circulation)',
    to: '/enseignant/ccs-jury',
  },
  {
    icon: ScrollText,
    titre: 'Modèle de fiche séance',
    sousTitre: 'Wizard de préparation 4 phases',
    to: '/enseignant/preparer-seance',
  },
  {
    icon: Sparkles,
    titre: 'Fiche séance auto-générée',
    sousTitre: 'Fiche pré-remplie via quiz interactif',
    to: '/enseignant/fiche-seance-live',
  },
];

/* ── Étape — carte parcours ─────────────────────────────────────── */
function EtapeCard({ etape }) {
  const Icon = etape.icon;
  return (
    <article
      className="rounded-2xl border-2 bg-card overflow-hidden hover:shadow-lg transition-all relative"
      style={{ borderColor: `${etape.accent}33` }}
      data-testid={`etape-${etape.n}`}
    >
      {etape.highlight && (
        <span
          className="absolute -top-2 right-4 z-10 rounded-full px-2.5 py-0.5 text-[10px] font-black uppercase tracking-widest text-white shadow"
          style={{ background: etape.accent }}
        >
          ★ Le plus utile
        </span>
      )}

      <header className="p-4 sm:p-5" style={{ background: `${etape.accent}0d` }}>
        <div className="flex items-start gap-3">
          {/* Badge numéro */}
          <div className="flex-shrink-0 flex flex-col items-center">
            <div
              className="w-12 h-12 sm:w-14 sm:h-14 rounded-2xl flex items-center justify-center text-white"
              style={{ background: etape.accent }}
            >
              <Icon className="w-6 h-6 sm:w-7 sm:h-7" />
            </div>
            <span
              className="mt-1 text-[10px] font-black uppercase tracking-widest"
              style={{ color: etape.accent }}
            >
              Étape {etape.n}
            </span>
          </div>

          <div className="flex-1 min-w-0">
            <h3
              className="text-lg sm:text-xl font-black leading-tight"
              style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
            >
              {etape.titre}
            </h3>
            <p className="text-xs sm:text-sm text-muted-foreground mt-1 leading-snug">
              {etape.sousTitre}
            </p>
          </div>
        </div>
      </header>

      {/* Cibles */}
      <div className="p-4 sm:p-5 pt-3">
        <ul className="space-y-1 mb-4">
          {etape.cibles.map((c) => (
            <li key={c} className="flex items-start gap-2 text-xs sm:text-sm">
              <Target
                className="w-3.5 h-3.5 flex-shrink-0 mt-0.5"
                style={{ color: etape.accent }}
              />
              <span className="text-muted-foreground">{c}</span>
            </li>
          ))}
        </ul>
        <Button
          asChild
          className="w-full gap-2 text-white font-black uppercase tracking-wider"
          style={{ background: etape.accent }}
          data-testid={`etape-cta-${etape.n}`}
        >
          <Link to={etape.to}>
            {etape.cta} <ArrowRight className="w-4 h-4" />
          </Link>
        </Button>
      </div>
    </article>
  );
}

/* ── Ressource annexe ───────────────────────────────────────────── */
function RessourceCard({ ressource }) {
  const Icon = ressource.icon;
  const isHighlight = ressource.highlight;
  return (
    <Link
      to={ressource.to}
      className={`relative rounded-xl border-2 p-3 sm:p-4 transition-colors flex items-start gap-3 group ${
        isHighlight
          ? 'bg-gradient-to-br from-amber-50 via-card to-orange-50/40 dark:from-amber-950/20 dark:to-orange-950/10 border-amber-400 hover:border-amber-500 shadow-md'
          : 'bg-card border-border hover:border-orange-400'
      }`}
      data-testid={`ressource-${ressource.to.replace(/[^a-z0-9]/gi, '-')}`}
    >
      {isHighlight && (
        <span
          className="absolute -top-2 right-3 z-10 rounded-full px-2 py-0.5 text-[9px] font-black uppercase tracking-widest text-white shadow"
          style={{ background: '#f59e0b' }}
        >
          ★ Nouveau
        </span>
      )}
      <div
        className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0"
        style={{
          background: isHighlight ? '#f59e0b22' : `${ACCENT}15`,
          color: isHighlight ? '#d97706' : ACCENT,
        }}
      >
        <Icon className="w-5 h-5" />
      </div>
      <div className="flex-1 min-w-0">
        <h4 className="text-sm font-black leading-tight">{ressource.titre}</h4>
        <p className="text-xs text-muted-foreground mt-0.5 leading-snug">
          {ressource.sousTitre}
        </p>
      </div>
      <ArrowRight className="w-4 h-4 text-muted-foreground group-hover:translate-x-0.5 group-hover:text-orange-500 transition-all flex-shrink-0 mt-1" />
    </Link>
  );
}

/* ── Page ───────────────────────────────────────────────────────── */
export default function PreparationCCS() {
  useSeo({
    title: 'Préparation CCS 2 Roues — Le hub du futur enseignant ECSR',
    description: 'Tout pour réussir le CCS 2 Roues (ECSR) : référentiel officiel, 27 études de cas, 105 questions jury, simulateur d\'oral avec IA, Grand Quiz et 22 fiches Jury Expert. Parcours en 6 étapes.',
    path: '/preparation-ccs',
  });

  return (
    <div className="min-h-screen pb-16" data-testid="preparation-ccs-page">
      {/* Hero */}
      <div
        className="px-4 pt-8 pb-14 text-white"
        style={{ background: `linear-gradient(135deg, ${ACCENT} 0%, #c2410c 60%, #9a3412 100%)` }}
      >
        <div className="max-w-4xl mx-auto">
          <div className="inline-flex items-center gap-2 rounded-full bg-white/15 backdrop-blur px-3 py-1 text-[10px] font-black uppercase tracking-widest mb-3">
            <GraduationCap className="w-3.5 h-3.5" /> Futur enseignant ECSR
          </div>
          <h1
            className="text-3xl sm:text-5xl font-black leading-tight mb-3"
            style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
          >
            Prépare et décroche le<br className="hidden sm:inline" /> CCS 2 Roues
          </h1>
          <p className="text-sm sm:text-base opacity-90 max-w-2xl leading-relaxed">
            Un parcours en <strong>6 étapes claires</strong> pour maîtriser le référentiel REMC,
            t&apos;entraîner sur les 27 études de cas officielles, simuler l&apos;oral avec un Jury Virtuel IA,
            et arriver le jour J <em>serein et opérationnel</em>.
          </p>

          <div className="flex flex-wrap gap-2 mt-5">
            <Button
              asChild
              className="bg-white text-orange-700 hover:bg-white/95 gap-2 font-black uppercase tracking-wider"
              data-testid="hero-cta-simulateur"
            >
              <Link to="/enseignant/simulateur-ccs">
                <FlaskConical className="w-4 h-4" /> Lancer un examen blanc
              </Link>
            </Button>
            <Button
              asChild
              variant="outline"
              className="bg-white/10 border-white text-white hover:bg-white/20 gap-2 font-black uppercase tracking-wider"
              data-testid="hero-cta-referentiel"
            >
              <Link to="/enseignant/referentiel">
                <BookOpen className="w-4 h-4" /> Voir le référentiel
              </Link>
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 -mt-8">
        {/* Bandeau cross-audience : oriente les enseignants déjà certifiés */}
        <AudienceContextBanner
          pageHint="Ce hub est conçu pour les candidats CCS 2 Roues. À quel public appartiens-tu ?"
          contexts={{
            'futur-enseignant': {
              title: 'Tu es au bon endroit',
              bullets: [
                'Suis le parcours 6 étapes dans l\'ordre : la difficulté monte progressivement.',
                'Le Simulateur Wizard est l\'outil-pivot — refais-le plusieurs fois.',
                'Pour chaque cas / question, mémorise les phrases-clés du jury.',
              ],
              cta: 'Lancer le Simulateur Wizard',
              ctaTo: '/enseignant/simulateur-ccs',
            },
            enseignant: {
              title: 'Déjà certifié CCS 2 Roues ?',
              intro: 'Ce hub est conçu pour les candidats. Pour tes outils terrain (Plateau, Circulation, Séance Live, statistiques élèves), va directement sur l\'Espace Pro.',
              bullets: [
                'Tu peux toujours réutiliser les fiches Jury Expert pour structurer tes propres cours.',
                'Les 27 études de cas servent aussi à coacher des collègues qui passent le CCS.',
              ],
              cta: 'Aller à l\'Espace Pro ECSR',
              ctaTo: '/enseignant',
            },
          }}
          showOnEleve={false}
          className="mb-6"
        />

        {/* Stats / repères */}
        <section
          className="rounded-2xl bg-card border-2 p-4 sm:p-5 shadow-lg mb-8"
          style={{ borderColor: `${ACCENT}33` }}
          data-testid="stats-bar"
        >
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            {[
              { v: '27', label: 'Études de cas' },
              { v: '105', label: 'Questions jury' },
              { v: '22', label: 'Fiches Jury Expert' },
              { v: '6', label: 'Modules CCS' },
            ].map((s) => (
              <div key={s.label} className="text-center">
                <div
                  className="text-2xl sm:text-3xl font-black tabular-nums"
                  style={{ color: ACCENT, fontFamily: "'Source Code Pro', monospace" }}
                >
                  {s.v}
                </div>
                <div className="text-[10px] sm:text-xs font-black uppercase tracking-widest text-muted-foreground mt-1">
                  {s.label}
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Parcours 6 étapes */}
        <section className="mb-10">
          <div className="flex items-end justify-between mb-4">
            <div>
              <p
                className="text-[10px] font-black uppercase tracking-widest"
                style={{ color: ACCENT }}
              >
                Ton parcours
              </p>
              <h2
                className="text-2xl sm:text-3xl font-black"
                style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
              >
                6 étapes pour décrocher le CCS
              </h2>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {ETAPES.map((etape) => (
              <EtapeCard key={etape.n} etape={etape} />
            ))}
          </div>
        </section>

        {/* Ressources complémentaires */}
        <section className="mb-10">
          <div className="mb-4">
            <p
              className="text-[10px] font-black uppercase tracking-widest"
              style={{ color: ACCENT }}
            >
              Pour aller plus loin
            </p>
            <h2
              className="text-xl sm:text-2xl font-black"
              style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
            >
              Ressources & outils complémentaires
            </h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {RESSOURCES.map((r) => (
              <RessourceCard key={r.to} ressource={r} />
            ))}
          </div>
        </section>

        {/* Chiffres clés sécurité routière — utiles pour citer face au jury */}
        <section className="space-y-3">
          <header className="text-center">
            <p className="text-[10px] font-black uppercase tracking-widest text-muted-foreground">
              Argument jury · Données officielles
            </p>
            <h2
              className="text-xl sm:text-2xl font-black"
              style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
            >
              Cite les bons chiffres face au jury
            </h2>
          </header>
          <SecuriteRoutiereStats variant="jury" />
        </section>

        {/* CTA cross-audience */}
        <section
          className="rounded-2xl p-5 sm:p-6 text-center"
          style={{ background: `${ACCENT}10`, borderLeft: `4px solid ${ACCENT}` }}
        >
          <h3 className="text-lg sm:text-xl font-black mb-2">
            Déjà certifié CCS 2 Roues ?
          </h3>
          <p className="text-sm text-muted-foreground mb-4 max-w-2xl mx-auto">
            Accède aux outils terrain pro : séance live, exercices plateau, fiches REMC,
            historique de tes séances et bien plus.
          </p>
          <Button asChild variant="outline" className="gap-2" data-testid="cross-cta-enseignant">
            <Link to="/enseignant">
              Espace enseignant <ArrowRight className="w-4 h-4" />
            </Link>
          </Button>
        </section>

        <p className="text-[11px] italic text-muted-foreground text-center pt-6">
          Code 2 Roues · Hub de préparation CCS 2 Roues conforme au référentiel TP-01303 (JO 28/04/2016).
        </p>
      </div>
    </div>
  );
}
